import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { ChevronDown, Globe } from "lucide-react";

const LANGS = [
    { code: "en", label: "EN", full: "English" },
    { code: "ro", label: "RO", full: "Română" },
];

export const LanguageSelector = ({ variant = "desktop" }) => {
    const { i18n } = useTranslation();
    const navigate = useNavigate();
    const { lang } = useParams();
    const [open, setOpen] = useState(false);
    const ref = useRef(null);

    const current = LANGS.find((l) => l.code === (lang || i18n.language)) || LANGS[0];

    const change = (code) => {
        i18n.changeLanguage(code);
        try {
            localStorage.setItem("aima_lang", code);
        } catch (_) { /* noop */ }
        const parts = window.location.pathname.split("/").filter(Boolean);
        parts[0] = code;
        navigate("/" + parts.join("/") + window.location.hash, { replace: false });
        setOpen(false);
    };

    useEffect(() => {
        const onClick = (e) => {
            if (!ref.current || ref.current.contains(e.target)) return;
            setOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    if (variant === "mobile") {
        return (
            <div className="flex gap-2" data-testid="lang-selector-mobile">
                {LANGS.map((l) => {
                    const active = current.code === l.code;
                    return (
                        <button
                            key={l.code}
                            onClick={() => change(l.code)}
                            data-testid={`lang-${l.code}`}
                            className={`h-9 px-3 font-mono text-[11px] uppercase tracking-[0.22em] border transition-colors ${
                                active
                                    ? "bg-white text-black border-white"
                                    : "border-white/10 text-white/60 hover:text-white hover:border-white/25"
                            }`}
                        >
                            {l.label}
                        </button>
                    );
                })}
            </div>
        );
    }

    return (
        <div ref={ref} className="relative" data-testid="lang-selector">
            <button
                onClick={() => setOpen((v) => !v)}
                data-testid="lang-selector-button"
                aria-haspopup="listbox"
                aria-expanded={open}
                className="inline-flex items-center gap-2 h-9 px-3 border border-white/10 hover:border-white/25 text-white/75 hover:text-white transition-colors font-mono text-[11px] uppercase tracking-[0.22em]"
            >
                <Globe className="w-3.5 h-3.5" strokeWidth={1.6} />
                {current.label}
                <ChevronDown className={`w-3 h-3 transition-transform ${open ? "rotate-180" : ""}`} strokeWidth={2} />
            </button>
            {open && (
                <ul
                    role="listbox"
                    className="absolute right-0 mt-2 w-40 border border-white/10 bg-graphite/95 backdrop-blur-md shadow-2xl z-50"
                >
                    {LANGS.map((l) => {
                        const active = current.code === l.code;
                        return (
                            <li key={l.code}>
                                <button
                                    onClick={() => change(l.code)}
                                    data-testid={`lang-option-${l.code}`}
                                    role="option"
                                    aria-selected={active}
                                    className={`w-full flex items-center justify-between px-4 h-11 text-left transition-colors ${
                                        active ? "bg-white/[0.05] text-white" : "text-white/65 hover:text-white hover:bg-white/[0.03]"
                                    }`}
                                >
                                    <span className="font-plex text-sm">{l.full}</span>
                                    <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-white/40">
                                        {l.label}
                                    </span>
                                </button>
                            </li>
                        );
                    })}
                </ul>
            )}
        </div>
    );
};

export default LanguageSelector;
